// src/RiskScoreModal.js
// Full-screen overlay: 9-question JBF Risk Score questionnaire → score output.
import { useState } from 'react';
import {
  RISK_CATEGORIES, RISK_QUESTIONS, computeRiskScore, isComplete, riskNarrative,
} from './riskScore';

export function fmtRiskDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
}

export function RiskScoreModal({ existing, clientName, onSave, onClose }) {
  const [answers, setAnswers] = useState(() => existing?.answers || {});
  const [view, setView] = useState('quiz'); // 'quiz' | 'result'

  const answeredCount = RISK_QUESTIONS.filter(q => Number.isInteger(answers[q.id])).length;
  const complete = isComplete(answers);

  const select = (qid, idx) => setAnswers(prev => ({ ...prev, [qid]: idx }));

  const result = view === 'result' ? computeRiskScore(answers) : null;
  const narrative = result ? riskNarrative(result, clientName) : '';

  function handleSave() {
    const rs = computeRiskScore(answers);
    onSave({ ...rs, completedAt: new Date().toISOString(), answers });
  }

  return (
    <div className="risk-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="risk-modal" role="dialog" aria-modal="true" aria-label="JBF Risk Score">
        <div className="risk-modal-head">
          <div>
            <div className="risk-modal-title">JBF Risk Score</div>
            <div className="risk-modal-sub">{clientName || 'Prospect'}</div>
          </div>
          <button className="risk-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {view === 'quiz' ? (
          <>
            <div className="risk-modal-body">
              {RISK_CATEGORIES.map(cat => (
                <div className="risk-cat" key={cat.key}>
                  <div className="risk-cat-label">{cat.label}</div>
                  {RISK_QUESTIONS.filter(q => q.category === cat.key).map((q, i) => {
                    const qNum = RISK_QUESTIONS.indexOf(q) + 1;
                    return (
                      <div className="risk-q" key={q.id}>
                        <div className="risk-q-prompt"><span className="risk-q-num">Q{qNum}</span>{q.prompt}</div>
                        <div className="risk-q-options">
                          {q.options.map((opt, idx) => {
                            const sel = answers[q.id] === idx;
                            return (
                              <button
                                type="button"
                                key={idx}
                                className={`risk-opt${sel ? ' risk-opt-sel' : ''}`}
                                onClick={() => select(q.id, idx)}
                              >
                                <span className="risk-opt-radio" aria-hidden />
                                <span className="risk-opt-label">{opt.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="risk-modal-foot">
              <div className="risk-progress">{answeredCount} of {RISK_QUESTIONS.length} answered</div>
              <div className="risk-foot-btns">
                <button className="risk-btn-ghost" onClick={onClose}>Cancel</button>
                <button className="risk-btn-primary" disabled={!complete} onClick={() => setView('result')}>
                  Calculate Score
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="risk-modal-body risk-result-body">
              <div className="risk-score-hero">
                <div className="risk-score-num">{result.composite}</div>
                <div className={`risk-band risk-band-${result.band.toLowerCase()}`}>{result.band}</div>
                <div className="risk-score-scale">out of 100</div>
              </div>

              <div className="risk-sub-grid">
                {[
                  { label: 'Loss Tolerance', val: result.lossToleranceScore },
                  { label: 'Risk Capacity', val: result.riskCapacityScore },
                  { label: 'Roth Context', val: result.rothContextScore },
                ].map(s => (
                  <div className="risk-sub-card" key={s.label}>
                    <div className="risk-sub-label">{s.label}</div>
                    <div className="risk-sub-val">{s.val}</div>
                    <div className="risk-sub-bar"><div className="risk-sub-fill" style={{ width: `${s.val}%` }} /></div>
                  </div>
                ))}
              </div>

              <div className={`risk-ce-flag risk-ce-${result.ceSignal.level}`}>
                <span className="risk-ce-dot" />
                <span className="risk-ce-text">{result.ceSignal.text}</span>
              </div>

              <div className="risk-narrative">{narrative}</div>

              <div className="risk-completed">Completed {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div className="risk-modal-foot">
              <button className="risk-btn-ghost" onClick={() => setView('quiz')}>← Back to questions</button>
              <div className="risk-foot-btns">
                <button className="risk-btn-primary" onClick={handleSave}>Save to Profile</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
